import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Check, Loader2, Lock, Sparkles, Zap } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { TrustBadges } from "@/components/TrustBadges";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { track } from "@/lib/meta";

const INCLUDED = [
  "AI proposal generation (wizard + transcript)",
  "All 15 editor blocks & drag-and-drop",
  "Unlimited brand kits & templates",
  "Client CRM, multi-currency & GST",
  "Share links, tracking & e-signature",
  "PDF & Word export, analytics & CSV",
];


export default function Plans() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [catalogue, setCatalogue] = useState(null);
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");
  const [licence, setLicence] = useState("");
  const [creditQty, setCreditQty] = useState(25);


  const load = () => {
    api.get("/billing/plans").then((r) => setCatalogue(r.data));
    api.get("/billing/subscription").then((r) => setSub(r.data));
  };
  useEffect(() => {
    track(
      "ViewContent",
      { content_type: "product_group", content_name: "Plans", content_category: "pricing" },
      { eventId: `vc_plans_${window.location.pathname}_${Math.floor(Date.now() / 60000)}` },
    );
    load();
    const chosen = sessionStorage.getItem("pj_plan");
    if (chosen) {
      sessionStorage.removeItem("pj_plan");
      navigate(`/checkout?plan=${chosen}`, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  const applyLicence = async () => {
    try {
      const { data } = await api.post("/billing/manual-license", { plan: "yearly", license_code: licence });
      setSub(data);
      toast.success("Licence applied");
      navigate("/dashboard");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!catalogue || !sub)
    return (
      <div className="min-h-screen grid place-items-center bg-app text-zinc-600">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );

  return (
    <div className="min-h-screen nv-grid-bg text-zinc-100">
      <div className="mx-auto max-w-5xl px-5 py-14">
        <div className="flex items-center gap-2.5 mb-10">
          <div className="w-8 h-8 rounded-lg bg-indigo-500 grid place-items-center">
            <Sparkles className="w-4 h-4 text-white" />
          </div>
          <span className="font-display font-extrabold tracking-tight">Propojo</span>
          <button data-testid="plans-skip-btn" onClick={() => navigate("/dashboard")} className="ml-auto text-sm text-zinc-400 hover:text-zinc-200">
            Continue on trial →
          </button>
        </div>

        <p className="text-[11px] font-num uppercase tracking-[0.22em] text-indigo-400">Choose your plan</p>
        <h1 className="mt-3 text-3xl sm:text-4xl font-extrabold tracking-tight">Pick a plan and start sending.</h1>
        <p className="mt-2 text-sm text-zinc-400">
          Every plan includes every feature. Longer terms give you more proposal credits per rupee.
        </p>

        <div data-testid="subscription-status" className="mt-6 nv-card p-4 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <span className="text-zinc-400">
            Current: <span className="text-zinc-100 font-medium">{sub.plan_label}</span>
          </span>
          <span className={`font-num uppercase text-xs px-2 py-0.5 rounded-full border ${sub.read_only ? "border-red-500/30 text-red-300" : "border-emerald-500/30 text-emerald-300"}`}>
            {sub.status}
          </span>
          <span className="text-zinc-400 font-num">
            {sub.proposals_used}/{sub.proposal_limit} credits used
          </span>
          {sub.period_end && (
            <span className="text-zinc-500 font-num text-xs">renews {new Date(sub.period_end).toLocaleDateString()}</span>
          )}
        </div>

        <div className="mt-6 grid md:grid-cols-3 gap-4">
          {catalogue.plans.map((plan) => (
            <div
              key={plan.key}
              data-testid={`plan-card-${plan.key}`}
              className={`relative nv-card p-6 flex flex-col ${plan.popular ? "border-indigo-500/40" : ""}`}
            >
              {plan.popular && (
                <span className="absolute -top-3 left-6 rounded-full bg-indigo-500 px-3 py-1 text-[10px] font-num uppercase tracking-[0.16em] text-white">
                  Most popular
                </span>
              )}
              <p className="text-[11px] font-num uppercase tracking-[0.18em] text-zinc-500">{plan.label}</p>
              <p className="mt-3 text-3xl font-extrabold font-num tracking-tight">₹{plan.amount_inr.toLocaleString("en-IN")}</p>
              <p className="text-xs text-zinc-500">
                per {plan.months === 1 ? "month" : plan.months === 3 ? "quarter" : "year"}
                {plan.save_pct > 0 && <span className="text-emerald-400 font-num"> · save {plan.save_pct}%</span>}
              </p>
              <p className="mt-4 text-sm text-zinc-300 font-num">{plan.credits} proposals</p>
              <p className="text-xs text-zinc-500">{plan.seats} seats included</p>
              <ul className="mt-4 space-y-1.5 flex-1">
                {INCLUDED.map((f) => (
                  <li key={f} className="flex gap-2 text-xs text-zinc-400">
                    <Check className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`checkout-${plan.key}-btn`}
                onClick={() => navigate(`/checkout?plan=${plan.key}`)}
                className="mt-6 w-full h-11 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium inline-flex items-center justify-center gap-2 transition-colors"
              >
                <Lock className="w-3.5 h-3.5" />
                Pay ₹{plan.amount_inr.toLocaleString("en-IN")} securely
              </button>
            </div>
          ))}
        </div>

        <TrustBadges className="mt-6" />

        <div className="mt-8 nv-card p-5 max-w-xl" data-testid="credit-topup-card">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-indigo-400" />
            <p className="text-sm font-semibold">Top up credits</p>
          </div>
          <p className="mt-1 text-xs text-zinc-500">
            1 credit = 1 proposal · ₹{catalogue.credit_price_inr} per credit · minimum 25 credits. Credits never expire while your plan is active.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {[25, 50, 100, 250].map((q) => (
              <button
                key={q}
                data-testid={`credit-pack-${q}`}
                onClick={() => setCreditQty(q)}
                className={`px-3 h-9 rounded-lg border text-xs font-num transition-colors ${
                  creditQty === q ? "border-indigo-500/45 bg-indigo-500/12 text-indigo-300" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"
                }`}
              >
                {q} credits · ₹{(q * catalogue.credit_price_inr).toLocaleString("en-IN")}
              </button>
            ))}
          </div>
          <div className="mt-3 flex gap-2 items-center">
            <Input
              data-testid="credit-quantity-input"
              type="number"
              min={25}
              max={5000}
              value={creditQty}
              onChange={(e) => setCreditQty(Math.max(25, Math.min(5000, Number(e.target.value) || 25)))}
              className="bg-zinc-900/70 border-zinc-800 font-num text-sm max-w-[120px]"
            />
            <button
              data-testid="buy-credits-btn"
              onClick={() => navigate(`/checkout?credits=${creditQty}`)}
              className="h-10 px-4 rounded-lg bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium inline-flex items-center gap-2 transition-colors"
            >
              <Lock className="w-3.5 h-3.5" />
              Pay ₹{(creditQty * catalogue.credit_price_inr).toLocaleString("en-IN")} securely
            </button>
            <span className="text-xs text-zinc-500 font-num">
              balance {sub.proposals_left}
              {sub.extra_credits > 0 ? ` (+${sub.extra_credits} topped up)` : ""}
            </span>
          </div>
        </div>

        <div className="mt-8 nv-card p-4 max-w-md">
          <p className="text-sm text-zinc-300">Have a licence code?</p>
          <div className="mt-2 flex gap-2">
            <Input data-testid="licence-code-input" value={licence} onChange={(e) => setLicence(e.target.value)} placeholder="PROPOJO-…" className="bg-zinc-900/70 border-zinc-800 font-num text-sm" />
            <Button data-testid="apply-licence-btn" variant="outline" onClick={applyLicence} disabled={!licence} className="border-zinc-800 text-zinc-300 shrink-0">
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
